/**
 * SaveManager — versioned localStorage persistence.
 *
 * Two namespaces live under two separate keys:
 *   - runProgress:  resets on prestige (biomass, upgrade levels, equipped mutations,
 *                   elapsed run time, unlocked codex entries for this run)
 *   - metaProgress: persists across prestige (DNA currency, permanently unlocked
 *                   mutations, codex entries, cumulative stats, future prestige
 *                   tree nodes)
 *
 * Schema evolution: every save includes a `version` number. On load, the file
 * is upgraded through the `migrations` array until it reaches CURRENT_VERSION.
 * To add a migration: bump CURRENT_VERSION and push a function that mutates
 * a save from version N to N+1.
 *
 * Phase 2 plug-in points:
 *   - prestige: flush runProgress, keep metaProgress. Call `resetRunProgress()`.
 *   - new stages: add keys under runProgress.stageData[stageId].
 */

import { bus } from './EventBus';
import { randomId, randomPlayerName } from '@/utils/names';
import { compareProgress, type ScoreProgress } from '@/score/formula';

export const CURRENT_VERSION = 10;

const KEY_RUN = 'evolve.runProgress';
const KEY_META = 'evolve.metaProgress';
/** Player identity + best scoreboard progress. Its own key so no game
 *  action (New Game, level jump, a failed migration) can wipe it. */
const KEY_PLAYER = 'evolve.player';

export interface PlayerRecord {
  playerId: string;
  playerName: string;
  /** Best-ever scoreboard progress from any ranked playthrough in this browser. */
  best: ScoreProgress;
}

export interface RunProgress {
  version: number;
  stageId: string;
  elapsed: number; // seconds in current stage
  biomass: number;
  hp: number;
  atp: number;
  upgrades: Record<string, number>; // upgradeId -> level
  /** Permanent in-run mutation ranks. mutationId -> rank (1..maxRank). */
  mutationRanks: Record<string, number>;
  /** How many mutation slots the player has paid to open this run. Starts at 1. */
  slotsOwned: number;
  codexUnlockedThisRun: string[];
  eventsSurvived: number;
  stageData: Record<string, Record<string, unknown>>;
  /** Scoreboard clock: seconds played in this stage, across deaths and
   *  reloads. Resets only when a stage starts fresh. */
  scoreTime: number;
}

export interface MetaProgress {
  version: number;
  dna: number;
  unlockedMutations: string[];
  codexUnlocked: string[];
  /** Codex entries whose first-encounter spotlight tutorial has fired.
   *  Distinct from codexUnlocked (which records contact); this tracks
   *  whether the player has been shown the "look at this thing" popup,
   *  so we don't pause+spotlight the same predator twice. Reset on
   *  explicit New Game alongside codexUnlocked. */
  codexTutorialized: string[];
  /** The Cambrian first-run tutorial (eat / dodge / what to eat) is done. */
  cambrianTutorialDone?: boolean;
  totalBiomassEver: number;
  totalRuns: number;
  settings: {
    volume: number;
    muted: boolean;
    zoom?: number;
  };
  /** Genome Shards — meta currency earned from achievements. Permanent. */
  shards: number;
  /** Earned achievement ids. Order = grant order. */
  achievements: string[];
  /** Cumulative counters used by AchievementSystem to evaluate triggers. */
  achievementProgress: AchievementProgress;
  /** Spent shards on the heritage tree. Map of metaUpgradeId -> level. */
  metaUpgrades: Record<string, number>;
  // prestige: future home of permanent tree nodes
  prestige: Record<string, unknown>;
  /** Current playthrough's scoreboard progress (only grows within it).
   *  `unranked` = started by a level jump or touched by a dev cheat —
   *  never tracked or sent. Identity + best live in PlayerRecord. */
  scoreProgress: ScoreProgress & { unranked: boolean };
}

export function emptyScoreProgress(): MetaProgress['scoreProgress'] {
  return {
    cellMaxBiomass: 0,
    cellTimeSec: null,
    multicellMaxBiomass: 0,
    multicellTimeSec: null,
    dna: 0,
    achievements: 0,
    unranked: false,
  };
}

export interface AchievementProgress {
  /** Cumulative absorbs per food id (across all runs). */
  foodAbsorbed: Record<string, number>;
  /** Cumulative predator kills (any). */
  predatorsKilled: number;
  /** Cumulative kills per predator id. */
  predatorsKilledById: Record<string, number>;
  /** Peak same-food streak ever reached. */
  peakStreak: number;
  /** Peak bloom-clear combo ever reached. */
  peakCombo: number;
  /** Stage ids cleared at least once. */
  stagesCleared: string[];
  /** Per-stage best clear time in seconds (smallest = best). */
  bestStageTime: Record<string, number>;
  /** Stage ids cleared without dying in the run that cleared them. */
  noDeathClears: string[];
  /** Cambrian counters (optional: older saves lack them). Creatures eaten
   *  by tier ('all' = total), peaks, and cumulative counts. */
  cambrianEaten?: Record<string, number>;
  peakEvolutions?: number;
  peakPartsMaxed?: number;
  peakFrenzy?: number;
  perfectLines?: number;
  apexKills?: number;
}

type Migration<T> = (save: any) => T;

/** Run-progress migrations. Index N migrates version N -> N+1. */
const runMigrations: Migration<RunProgress>[] = [
  // index 0 (v0 -> v1): never shipped, here so indices line up with version
  (save) => ({ ...save, version: 1 }),
  // index 1 (v1 -> v2): no run-side changes, bump to stay in lockstep with meta
  (save) => ({ ...save, version: 2 }),
  // index 2 (v2 -> v3): mutation slot economy. Existing runs grandfather in
  // with both slots open and swaps enabled so a player mid-run isn't suddenly
  // locked out of a build they already committed to.
  (save) => ({
    ...save,
    slotsUnlocked: typeof save.slotsUnlocked === 'number' ? save.slotsUnlocked : 2,
    swapsEnabled: save.swapsEnabled ?? true,
    version: 3,
  }),
  // index 3 (v3 -> v4): evolution-tree economy. Drops equipping/swaps in
  // favor of permanent picks + ranks. The new system is fundamentally
  // different from the old one, so in-flight runs are reset cleanly rather
  // than fudging a half-translation.
  (_save) => ({
    ...defaultRunProgress(),
    version: 4,
  }),
  // index 4 (v4 -> v5): no run-side changes; bump in lockstep with meta.
  (save) => ({ ...save, version: 5 }),
  // index 5 (v5 -> v6): no run-side changes; bump in lockstep with meta.
  (save) => ({ ...save, version: 6 }),
  // index 6 (v6 -> v7): no run-side changes; bump in lockstep with meta.
  (save) => ({ ...save, version: 7 }),
  // index 7 (v7 -> v8): Stage 3 "Cambrian" arrives. Initializes
  // stageData.cambrian as an empty container so the stage can write
  // run summaries / equipped mutations without an undefined check.
  (save) => ({
    ...save,
    stageData: {
      ...(save.stageData ?? {}),
      cambrian: (save.stageData?.cambrian) ?? {},
    },
    version: 8,
  }),
  // index 8 (v8 -> v9): scoreboard. The in-flight run's clock seeds from
  // its elapsed time; DNA collected starts counting from here.
  (save) => ({
    ...save,
    scoreTime: typeof save.elapsed === 'number' ? save.elapsed : 0,
    version: 9,
  }),
  // index 9 (v9 -> v10): unified scoreboard. dnaCollected moved to meta;
  // `unranked` is left in place for LeaderboardSystem's one-time adoption.
  (save) => {
    const { dnaCollected: _dna, ...rest } = save;
    return { ...rest, version: 10 };
  },
];

/** Meta-progress migrations. Index N migrates version N -> N+1. */
const metaMigrations: Migration<MetaProgress>[] = [
  // index 0 (v0 -> v1): never shipped
  (save) => ({ ...save, version: 1 }),
  // index 1 (v1 -> v2): start games unmuted. Old saves defaulted to
  // muted:true, which made players assume audio was broken.
  (save) => ({
    ...save,
    settings: { ...(save.settings ?? { volume: 0.5 }), muted: false },
    version: 2,
  }),
  // index 2 (v2 -> v3): meta-side bump in lockstep with run schema
  // for the slot-economy migration. No meta-field changes.
  (save) => ({ ...save, version: 3 }),
  // index 3 (v3 -> v4): no meta-field changes; bump to stay in lockstep
  // with the v4 run schema (evolution tree economy).
  (save) => ({ ...save, version: 4 }),
  // index 4 (v4 -> v5): introduce codexTutorialized — first-encounter
  // spotlight tracking. Old saves get an empty list, so existing
  // codex entries replay their spotlight on next encounter (acceptable
  // since the popup is a one-shot and dismisses with a click).
  (save) => ({ ...save, codexTutorialized: [], version: 5 }),
  // index 5 (v5 -> v6): introduce achievements + Genome Shards meta
  // currency + heritage-tree levels. Existing players start fresh with
  // zero shards and an empty achievement list — the next eligible
  // milestone they hit will grant retroactively (since the triggers are
  // based on cumulative meta state, not "first time seen").
  (save) => ({
    ...save,
    shards: 0,
    achievements: [],
    achievementProgress: emptyAchievementProgress(),
    metaUpgrades: {},
    version: 6,
  }),
  // index 6 (v6 -> v7): one-shot wipe of achievement state. The v6
  // matcher used meta.totalBiomassEver for biomassReached triggers,
  // which retro-granted phase1.first_steps + midway + target on the
  // first event of any new game (= 6 phantom shards). The matcher is
  // fixed in this release; this migration clears the bug residue so
  // affected players boot into a clean ledger.
  (save) => ({
    ...save,
    shards: 0,
    achievements: [],
    achievementProgress: emptyAchievementProgress(),
    metaUpgrades: {},
    version: 7,
  }),
  // index 7 (v7 -> v8): meta-side bump in lockstep with the v8 run
  // schema for Stage 3 "Cambrian". No meta-field changes — Cambrian
  // run-state lives entirely under run.stageData.cambrian.
  (save) => ({ ...save, version: 8 }),
  // index 8 (v8 -> v9): scoreboard identity for existing players.
  (save) => ({
    ...save,
    playerId: randomId(),
    playerName: randomPlayerName(),
    version: 9,
  }),
  // index 9 (v9 -> v10): unified scoreboard progress replaces the per-stage
  // pending-score queue. Achievements earned so far count toward it.
  (save) => {
    const { pendingScores: _pending, ...rest } = save;
    return {
      ...rest,
      scoreProgress: {
        ...emptyScoreProgress(),
        achievements: Array.isArray(save.achievements) ? save.achievements.length : 0,
      },
      version: 10,
    };
  },
];

export function defaultRunProgress(stageId = 'cell'): RunProgress {
  return {
    version: CURRENT_VERSION,
    stageId,
    elapsed: 0,
    biomass: 0,
    hp: 100,
    atp: 100,
    upgrades: {},
    mutationRanks: {},
    slotsOwned: 1,
    codexUnlockedThisRun: [],
    eventsSurvived: 0,
    stageData: {},
    scoreTime: 0,
  };
}

export function defaultMetaProgress(): MetaProgress {
  return {
    version: CURRENT_VERSION,
    dna: 0,
    unlockedMutations: [],
    codexUnlocked: [],
    codexTutorialized: [],
    totalBiomassEver: 0,
    totalRuns: 0,
    settings: { volume: 0.5, muted: false },
    shards: 0,
    achievements: [],
    achievementProgress: emptyAchievementProgress(),
    metaUpgrades: {},
    prestige: {},
    scoreProgress: emptyScoreProgress(),
  };
}

export function emptyAchievementProgress(): AchievementProgress {
  return {
    foodAbsorbed: {},
    predatorsKilled: 0,
    predatorsKilledById: {},
    peakStreak: 0,
    peakCombo: 0,
    stagesCleared: [],
    bestStageTime: {},
    noDeathClears: [],
    cambrianEaten: {},
    peakEvolutions: 0,
    peakPartsMaxed: 0,
    peakFrenzy: 0,
    perfectLines: 0,
    apexKills: 0,
  };
}

function migrate<T>(raw: any, migrations: Migration<T>[], fallback: () => T): T {
  if (!raw || typeof raw !== 'object') return fallback();
  let v = typeof raw.version === 'number' ? raw.version : 0;
  let save: any = raw;
  while (v < CURRENT_VERSION && migrations[v]) {
    save = migrations[v](save);
    v += 1;
    save.version = v;
  }
  if (v !== CURRENT_VERSION) {
    console.warn(`[SaveManager] save at version ${v} could not be migrated; using defaults`);
    return fallback();
  }
  return save as T;
}

export class SaveManager {
  private run: RunProgress;
  private meta: MetaProgress;
  private player: PlayerRecord;
  /** Set while playing a jumped level: the real run + meta, held aside so
   *  the sandbox never overwrites what Continue resumes. */
  private mainSave: { run: RunProgress; meta: MetaProgress } | null = null;
  private saveTimer = 0;
  private autosaveInterval = 10; // seconds

  constructor() {
    this.run = this.loadRun();
    this.meta = this.loadMeta();
    this.player = this.loadPlayer();
    this.save();

    // flush on tab close (best-effort — synchronous localStorage call)
    window.addEventListener('beforeunload', () => this.save());
    // also when hidden (mobile backgrounding)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.save();
    });
  }

  getRun(): RunProgress { return this.run; }
  getMeta(): MetaProgress { return this.meta; }
  getPlayer(): PlayerRecord { return this.player; }

  /** Replace the run progress in-memory (e.g. on load or new run). */
  setRun(run: RunProgress): void { this.run = run; }
  setMeta(meta: MetaProgress): void { this.meta = meta; }

  /** Call from the main loop; autosaves every `autosaveInterval` seconds. */
  tick(dt: number): void {
    this.saveTimer += dt;
    if (this.saveTimer >= this.autosaveInterval) {
      this.saveTimer = 0;
      this.save();
    }
  }

  save(): void {
    try {
      localStorage.setItem(KEY_PLAYER, JSON.stringify(this.player));
      if (this.mainSave) {
        // Jumped level = throwaway sandbox: never persisted. Re-save only
        // the main meta, whose settings (volume/mute/zoom) it shares.
        localStorage.setItem(KEY_META, JSON.stringify(this.mainSave.meta));
      } else {
        localStorage.setItem(KEY_RUN, JSON.stringify(this.run));
        localStorage.setItem(KEY_META, JSON.stringify(this.meta));
      }
    } catch (err) {
      console.warn('[SaveManager] save failed', err);
    }
  }

  /** Wipes the run save (used by prestige in Phase 2; also by explicit "Restart"). */
  resetRunProgress(stageId = 'cell'): void {
    this.run = defaultRunProgress(stageId);
    // Evolution-tree economy: DNA is a per-run currency, even though it
    // currently lives under meta for cross-reload persistence. Clearing
    // it here ensures "New Game" actually starts you at zero DNA.
    this.meta.dna = 0;
    // Codex unlocks live under meta so they survive death/reload. On an
    // explicit "New Game" we want a clean slate — including replaying the
    // intro beats — so wipe them here too.
    this.meta.codexUnlocked = [];
    this.meta.codexTutorialized = [];
    // New Game is a hard reset: the metaplay ledger (shards / earned
    // achievements / cumulative achievement counters / heritage levels)
    // ALL clear too, so a fresh run starts from a true zero. Shards
    // stay preserved across deaths within a run; only an explicit
    // New Game wipes them.
    this.meta.shards = 0;
    this.meta.achievements = [];
    this.meta.achievementProgress = emptyAchievementProgress();
    this.meta.metaUpgrades = {};
    this.save();
    bus.emit('save:loaded', undefined);
  }

  /**
   * Soft reset used when the player dies mid-run. Biomass/HP/ATP/elapsed are
   * reset, but upgrade levels, equipped mutations, and run-unlocked codex
   * entries are preserved — dying shouldn't erase player investment the way
   * prestige does.
   */
  resetAfterDeath(stageId = 'cell'): void {
    const keep = this.run;
    this.run = {
      ...defaultRunProgress(stageId),
      upgrades: keep.upgrades,
      mutationRanks: keep.mutationRanks,
      slotsOwned: keep.slotsOwned,
      codexUnlockedThisRun: keep.codexUnlockedThisRun,
      eventsSurvived: keep.eventsSurvived,
      stageData: keep.stageData,
      scoreTime: keep.scoreTime,
    };
    this.save();
    bus.emit('save:loaded', undefined);
  }

  /**
   * Start a new playthrough's scoreboard tracking. The player name and
   * best-ever progress are kept. `ranked` = false for level jumps.
   */
  resetPlaythroughProgress(ranked: boolean): void {
    this.recordBest();
    this.meta.scoreProgress = { ...emptyScoreProgress(), unranked: !ranked };
    this.save();
  }

  /**
   * Level jump: play `stageId` in a throwaway sandbox (fresh run + meta,
   * unranked). The main save — the run Continue resumes — stays untouched
   * in storage; reloading the page returns to it.
   */
  startJump(stageId: string): void {
    if (!this.mainSave) {
      this.save();
      this.mainSave = { run: this.run, meta: this.meta };
    }
    this.run = defaultRunProgress(stageId);
    this.meta = {
      ...defaultMetaProgress(),
      settings: this.mainSave.meta.settings,
      scoreProgress: { ...emptyScoreProgress(), unranked: true },
    };
    bus.emit('save:loaded', undefined);
  }

  /** Fold the current ranked playthrough into the best-ever record if it
   *  ranks higher. Returns true when the best changed. Written to storage
   *  immediately (it's tiny) so a crash or killed tab can't lose it. */
  recordBest(): boolean {
    const { unranked, ...current } = this.meta.scoreProgress;
    if (unranked || current.cellMaxBiomass <= 0) return false;
    if (compareProgress(current, this.player.best) >= 0) return false;
    this.player.best = current;
    try {
      localStorage.setItem(KEY_PLAYER, JSON.stringify(this.player));
    } catch (err) {
      console.warn('[SaveManager] player save failed', err);
    }
    return true;
  }

  /** Full wipe of game progress — used by a future "delete all data"
   *  option. The player record (name + best score) is deliberately kept. */
  hardReset(): void {
    localStorage.removeItem(KEY_RUN);
    localStorage.removeItem(KEY_META);
    this.run = defaultRunProgress();
    this.meta = defaultMetaProgress();
  }

  /** True if there's meaningful run progress worth resuming. */
  hasRunProgress(): boolean {
    return this.run.biomass > 0
      || this.run.elapsed > 0
      || this.run.scoreTime > 0
      || Object.keys(this.run.upgrades).length > 0
      || Object.keys(this.run.mutationRanks).length > 0;
  }

  private loadRun(): RunProgress {
    try {
      const raw = localStorage.getItem(KEY_RUN);
      if (!raw) return defaultRunProgress();
      return migrate(JSON.parse(raw), runMigrations, defaultRunProgress);
    } catch {
      return defaultRunProgress();
    }
  }

  private loadPlayer(): PlayerRecord {
    const { unranked: _u, ...emptyBest } = emptyScoreProgress();
    try {
      const raw = localStorage.getItem(KEY_PLAYER);
      const p = raw ? JSON.parse(raw) : null;
      if (p && typeof p.playerId === 'string' && typeof p.playerName === 'string') {
        return { playerId: p.playerId, playerName: p.playerName, best: { ...emptyBest, ...(p.best ?? {}) } };
      }
    } catch { /* corrupt — fall through to adopt / create */ }
    // First launch with a separate player record: adopt the identity the
    // meta save carried (v9–v10), or roll a new one for a brand-new browser.
    const legacy = this.meta as MetaProgress & { playerId?: unknown; playerName?: unknown };
    const record: PlayerRecord = {
      playerId: typeof legacy.playerId === 'string' ? legacy.playerId : randomId(),
      playerName: typeof legacy.playerName === 'string' ? legacy.playerName : randomPlayerName(),
      best: emptyBest,
    };
    delete legacy.playerId;
    delete legacy.playerName;
    return record;
  }

  private loadMeta(): MetaProgress {
    try {
      const raw = localStorage.getItem(KEY_META);
      if (!raw) return defaultMetaProgress();
      return migrate(JSON.parse(raw), metaMigrations, defaultMetaProgress);
    } catch {
      return defaultMetaProgress();
    }
  }
}

export const saveManager = new SaveManager();
